import { ColumnDef, CellContext } from '@tanstack/react-table'
import React from 'react'
import { Link } from 'react-router-dom'

import { toTitleCase } from '../../shared/Functions'
import type { CenterSubscriberFromApi } from '../../types'
import type { CenterEmployeeType } from '../../types/centerTypes'

// Type definitions for table data
interface ApplicationData {
  id: number
  centerName: string
  director: string
  coDirector: string | undefined
  centerType: string
}

interface EmailListData {
  id: number
  name: string
  description: string
  members: Array<{ id: number }>
}

interface ContactListData {
  id: number
  firstName: string
  lastName: string
  email: string
}

export const SharedApplicationColumns = (): ColumnDef<ApplicationData>[] => {
  return [
    {
      header: 'Center Name',
      accessorKey: 'centerName',
      cell: function centerNameCell(
        props: CellContext<ApplicationData, unknown>
      ) {
        return (
          <Link
            to={`/members/center_application/${props.row.original.id}`}
            className="font-bold link-text"
          >
            {props.getValue() as React.ReactNode}
          </Link>
        )
      }
    },
    {
      header: 'Director Name',
      accessorKey: 'director',
      cell: function directorNameCell(
        props: CellContext<ApplicationData, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: 'Co-Director Name',
      accessorKey: 'coDirector',
      cell: function coDirectorNameCell(
        props: CellContext<ApplicationData, unknown>
      ) {
        const value = props.getValue() as string | undefined
        if (value === '') {
          return 'None'
        } else {
          return value
        }
      }
    },
    {
      header: 'Center Type',
      accessorKey: 'centerType',
      cell: function centerTypeCell(
        props: CellContext<ApplicationData, unknown>
      ) {
        return toTitleCase(props.getValue() as string)
      }
    }
  ]
}

export const SharedEmployeeColumns = (): ColumnDef<CenterEmployeeType>[] => {
  return [
    {
      header: 'Name and Credentials',
      accessorKey: 'nameAndInfo',
      cell: function nameAndCredentialsCell(
        props: CellContext<CenterEmployeeType, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: 'Role',
      accessorKey: 'role',
      cell: function roleCell(props: CellContext<CenterEmployeeType, unknown>) {
        return toTitleCase(props.getValue() as string)
      }
    },
    {
      header: 'Employment Type',
      accessorKey: 'employeeType',
      cell: function employeeTypeCell(
        props: CellContext<CenterEmployeeType, unknown>
      ) {
        const value = props.getValue() as string | undefined
        if (value) {
          return toTitleCase(value)
        } else {
          return 'Unknown'
        }
      }
    }
  ]
}

export const SharedEmailListColumns = (): ColumnDef<EmailListData>[] => {
  return [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: function nameCell(props: CellContext<EmailListData, unknown>) {
        return (
          <Link
            to={`/members/contact_list/${props.row.original.id}`}
            className="font-bold link-text"
          >
            {toTitleCase(props.getValue() as string)}
          </Link>
        )
      }
    },
    {
      header: 'Description',
      accessorKey: 'description',
      cell: function descriptionCell(
        props: CellContext<EmailListData, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: 'Total # of Contacts',
      accessorKey: 'contactCount',
      cell: function contactCountCell(
        props: CellContext<EmailListData, unknown>
      ) {
        return props.row.original.members.length
      }
    }
  ]
}

export const SharedSubscriberListColumns =
  (): ColumnDef<CenterSubscriberFromApi>[] => {
    return [
      {
        header: 'Contact Name',
        accessorKey: 'firstName',
        cell: function contactNameCell(
          props: CellContext<CenterSubscriberFromApi, unknown>
        ) {
          return (
            (props.getValue() as string) + ' ' + props.row.original.lastName
          )
        }
      },
      {
        header: 'Contact Email',
        accessorKey: 'email',
        cell: function contactEmailCell(
          props: CellContext<CenterSubscriberFromApi, unknown>
        ) {
          return props.getValue() as string
        }
      },
      {
        header: 'Associated Center',
        accessorKey: '',
        cell: function associatedCenterCell(
          props: CellContext<CenterSubscriberFromApi, unknown>
        ) {
          if (props.row.original.centerId) {
            return (
              <p className="text-green-700">{props.row.original.centerName}</p>
            )
          }
          return <p className="text-red-500">Not Associated with a Center</p>
        }
      }
    ]
  }

export const SharedContactListColumns = (): ColumnDef<ContactListData>[] => {
  return [
    {
      header: 'Contact Name',
      accessorKey: 'firstName',
      cell: function contactNameCell(
        props: CellContext<ContactListData, unknown>
      ) {
        return (props.getValue() as string) + ' ' + props.row.original.lastName
      }
    },
    {
      header: 'Contact Email',
      accessorKey: 'email',
      cell: function contactEmailCell(
        props: CellContext<ContactListData, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: 'Actions',
      accessorKey: '',
      enableSorting: false,
      cell: function actionCell() {
        return <></>
      }
    }
  ]
}

export const ConsensusDetailsColumns = (): ColumnDef<
  Record<string, unknown>
>[] => {
  return [
    {
      header: () => (
        <div id="header-1" className="font-bold text-xl text-white">
          Category
        </div>
      ),
      accessorKey: 'title',
      enableSorting: true,
      cell: function contactNameCell(
        props: CellContext<Record<string, unknown>, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: () => (
        <div id="header-2" className="font-bold text-xl text-white">
          Consensus Statement
        </div>
      ),
      accessorKey: 'text',
      enableSorting: true,
      cell: function contactEmailCell(
        props: CellContext<Record<string, unknown>, unknown>
      ) {
        return props.getValue() as string
      }
    },
    {
      header: () => (
        <div id="header-3" className="font-bold text-xl text-white">
          Recommendation/Standard
        </div>
      ),
      accessorKey: 'typeOfRecommendation',
      enableSorting: true,
      cell: function contactEmailCell(
        props: CellContext<Record<string, unknown>, unknown>
      ) {
        return props.getValue() as string
      }
    }
  ]
}
